import type { OperatingSystem } from '@elfsight/lib-utils';
import DEFAULT_MACOS_CURSOR_ICON from '../icons/macos-cursor-arrow.png';
import DISABLED_MACOS_CURSOR_ICON from '../icons/macos-cursor-disabled.png';
import FINGER_MACOS_CURSOR_ICON from '../icons/macos-cursor-finger.png';
import TEXT_MACOS_CURSOR_ICON from '../icons/macos-cursor-text.png';
import DEFAULT_WINDOWS_CURSOR_ICON from '../icons/windows-cursor-arrow.png';
import DISABLED_WINDOWS_CURSOR_ICON from '../icons/windows-cursor-disabled.png';
import FINGER_WINDOWS_CURSOR_ICON from '../icons/windows-cursor-finger.png';
import TEXT_WINDOWS_CURSOR_ICON from '../icons/windows-cursor-text.png';
import type { A11yState } from '../types';
import { WIDGET_CLASS } from './constants';

type IconData = {
  icon: string;
  x: number;
  y: number;
};

type CursorIcons = {
  DEFAULT: IconData;
  DISABLED: IconData;
  FINGER: IconData;
  TEXT: IconData;
};

const CURSORS: Record<
  Exclude<OperatingSystem, 'linux' | 'unknown'>,
  CursorIcons
> = {
  macos: {
    DEFAULT: {
      icon: DEFAULT_MACOS_CURSOR_ICON,
      x: 1.8,
      y: 2.3
    },
    DISABLED: {
      icon: DISABLED_MACOS_CURSOR_ICON,
      x: 1.8,
      y: 2.3
    },
    FINGER: {
      icon: FINGER_MACOS_CURSOR_ICON,
      x: 6.5,
      y: 0.6
    },
    TEXT: {
      icon: TEXT_MACOS_CURSOR_ICON,
      x: 5.5,
      y: 10
    }
  },
  windows: {
    DEFAULT: {
      icon: DEFAULT_WINDOWS_CURSOR_ICON,
      x: 1.73,
      y: 1.5
    },
    DISABLED: {
      icon: DISABLED_WINDOWS_CURSOR_ICON,
      x: 1.73,
      y: 1.5
    },
    FINGER: {
      icon: FINGER_WINDOWS_CURSOR_ICON,
      x: 10,
      y: 1
    },
    TEXT: {
      icon: TEXT_WINDOWS_CURSOR_ICON,
      x: 5.5,
      y: 10.5
    }
  }
};

export function createStyles(
  {
    // text & readability
    easyReadFont,
    dyslexiaFriendlyFont,
    stopAnimation,

    // navigation
    keyboardFocus,
    largeClickableAreas,
    bigCursor,
    highlightLinks,

    // colors & contrast
    colorsContrast
  }: A11yState,
  os: Exclude<OperatingSystem, 'linux' | 'unknown'>
) {
  const currentCursorIconData = CURSORS[os] || CURSORS.windows;

  const highContrast = colorsContrast === 'high-contrast';
  const grayScale = colorsContrast === 'grayscale';
  const invertedColors = colorsContrast === 'inverted-colors';
  const colorBlind = colorsContrast === 'color-blind';
  const protanopia = colorsContrast === 'protanopia';
  const deuteranopia = colorsContrast === 'deuteranopia';
  const tritanopia = colorsContrast === 'tritanopia';

  return `
    ${dyslexiaFriendlyFont ? DYSLEXIA_FONT_STYLE : ''}

    html {
      ${highContrast ? `filter: contrast(125%) !important;` : ''}
      ${grayScale ? `filter: grayscale(100%) !important;` : ''}
      ${invertedColors ? `filter: invert(100%) !important;` : ''}
      ${colorBlind ? `filter: url(#colorblind-default) !important;` : ''}
      ${protanopia ? `filter: url(#protanopia) !important;` : ''}
      ${deuteranopia ? `filter: url(#deuteranopia) !important;` : ''}
      ${tritanopia ? `filter: url(#tritanopia) !important;` : ''}
      
      ${stopAnimation ? `scroll-behavior: auto !important;` : ''}
    }

    ${
      easyReadFont
        ? `
          body *:where(:not([class*="${WIDGET_CLASS}"], [class*="${WIDGET_CLASS}"] *, i, i *)) {
            font-family: 'Verdana' !important;
          }`
        : ''
    }

    ${
      stopAnimation
        ? `
          body *:not([class*="${WIDGET_CLASS}"] *) {
            transition: none !important;
            animation-fill-mode: forwards !important;
            animation-iteration-count: 1 !important;
            animation-duration: 0.001ms !important;
          }`
        : ''
    }

    ${
      keyboardFocus
        ? `
          body *:not([class*="${WIDGET_CLASS}"] *):focus-visible {
            outline: 3px solid rgb(25, 123, 255) !important; outline-offset: 2px !important;
          }`
        : ''
    }

    ${
      bigCursor
        ? `
        body * {
          cursor:url(${currentCursorIconData.DEFAULT.icon}) ${currentCursorIconData.DEFAULT.x} ${currentCursorIconData.DEFAULT.y}, default !important;
        }

        body *[disabled],
        body *[aria-disabled="true"],
        body *.disabled,
        body button:disabled *,
        body input:disabled,
        body select:disabled,
        body textarea:disabled,
        body fieldset:disabled {
          cursor: url(${currentCursorIconData.DISABLED.icon}) ${currentCursorIconData.DISABLED.x} ${currentCursorIconData.DISABLED.y}, not-allowed !important;
        }

        body *[style*="cursor: pointer"],
        body button,
        body button *,
        body [role="button"],
        body [role="button"] *,
        body [role="radio"],
        body [role="radio"] *,
        body [role="checkbox"],
        body [role="checkbox"] *,
        body [role="switch"],
        body [role="switch"] *,
        body a,
        body a *,
        body input[type="button"] *,
        body input[type="submit"],
        body input[type="reset"],
        body select,
        body label,
        body option {
          cursor: url(${currentCursorIconData.FINGER.icon}) ${currentCursorIconData.FINGER.x} ${currentCursorIconData.FINGER.y}, pointer !important;
        }

        body *[style*="cursor: text"],
        body input[type="text"],
        body input[type="password"],
        body input[type="email"],
        body input[type="number"],
        body input[type="search"],
        body input[type="tel"],
        body input[type="url"],
        body textarea {
          cursor: url(${currentCursorIconData.TEXT.icon}) ${currentCursorIconData.TEXT.x} ${currentCursorIconData.TEXT.y}, text !important;
        }
        `
        : ''
    }

    ${
      largeClickableAreas
        ? `
          button:not([class*="${WIDGET_CLASS}"] *),
          a:not([class*="${WIDGET_CLASS}"] *),
          input[type="button"]:not([class*="${WIDGET_CLASS}"] *),
          input[type="submit"]:not([class*="${WIDGET_CLASS}"] *),
          input[type="reset"]:not([class*="${WIDGET_CLASS}"] *),
          textarea:not([class*="${WIDGET_CLASS}"] *),
          select:not([class*="${WIDGET_CLASS}"] *),
          label:not([class*="${WIDGET_CLASS}"] *),
          option:not([class*="${WIDGET_CLASS}"] *) {
            min-width: 44px;
            min-height: 44px;
            align-content: center;
          }

          :where(li, nav):is(a, button, label):not([class*="${WIDGET_CLASS}"] *) {
            display: inline-block;
            max-width: 100%;
            white-space: normal;
            vertical-align: middle;
          }
        `
        : ''
    }

    ${
      highlightLinks
        ? `
          a:not([class*="${WIDGET_CLASS}"] *) {
            text-decoration: underline !important;
          }`
        : ''
    }
  `;
}

const DYSLEXIA_FONT_STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=Comic+Neue:wght@300;400;700&display=swap');

  @font-face {
    font-family: 'OpenDyslexic';
    src: url('https://acc-landing.vercel.app/fonts/OpenDyslexic-Bold.woff') format('woff');
    font-weight: bold;
    font-style: normal;
  }

  @font-face {
    font-family: 'OpenDyslexic';
    src: url('https://acc-landing.vercel.app/fonts/OpenDyslexic3-Bold.ttf') format('truetype');
    font-weight: bold;
    font-style: normal;
  }

  @font-face {
    font-family: 'OpenDyslexic';
    src: url('https://acc-landing.vercel.app/fonts/OpenDyslexic3-Regular.ttf') format('truetype');
    font-weight: normal;
    font-style: normal;
  }

  @font-face {
    font-family: 'OpenDyslexic';
    src: url('https://acc-landing.vercel.app/fonts/OpenDyslexic-Regular.woff') format('woff');
    font-weight: normal;
    font-style: normal;
  }

  @font-face {
    font-family: 'OpenDyslexic';
    src: url('https://acc-landing.vercel.app/fonts/OpenDyslexic-Regular.otf') format('opentype');
    font-weight: normal;
    font-style: normal;
  }

  @font-face {
    font-family: 'OpenDyslexic';
    src: url('https://acc-landing.vercel.app/fonts/OpenDyslexic-Bold.otf') format('opentype');
    font-weight: bold;
    font-style: normal;
  }

  body *:where(:not([class*="${WIDGET_CLASS}"], [class*="${WIDGET_CLASS}"] *, i, i *)) {
    font-family: OpenDyslexic, "Comic Neue", Arial, Helvetica, sans-serif !important;
  }
`;
